import {
  Alert,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'
import { sectionErrorText } from '../../shared/section-form'
import type {
  LadderKind,
  LadderReview,
  PerSlugRow,
  SectionForm,
} from '../../shared/section-form'
import PerOptionTierEditor from './PerOptionTierEditor'

export interface TierLadderEditorProps {
  form: SectionForm
  review: LadderReview
  perSlugRows: PerSlugRow[]
  tierCount?: number
  error?: string
  onLadderKindChange: (kind: LadderKind) => void
  onPerSlugChange: (key: string, tier: number) => void
  onSetAll: (tier: number) => void
}

export default function TierLadderEditor({
  form,
  review,
  perSlugRows: rows,
  tierCount,
  error,
  onLadderKindChange,
  onPerSlugChange,
  onSetAll,
}: TierLadderEditorProps) {
  const { formatMessage } = useIntl()

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2" component="h3">
        {formatMessage({ id: 'avatarStudio.editor.whoGetsIt' })}
      </Typography>

      <RadioGroup
        row={true}
        // A stored count ladder shows as per-option: the editor no longer offers count, but the
        // rule is left alone until a tier is actually changed.
        value={form.ladderKind === 'none' ? 'none' : 'slugs'}
        onChange={(e) => onLadderKindChange(e.target.value as LadderKind)}
      >
        {(['none', 'slugs'] as LadderKind[]).map((kind) => (
          <FormControlLabel
            key={kind}
            value={kind}
            control={<Radio size="small" />}
            label={formatMessage({ id: `avatarStudio.editor.ladder.${kind}` })}
          />
        ))}
      </RadioGroup>

      {form.ladderKind === 'none' && (
        <Typography variant="body2" color="text.secondary">
          {formatMessage(
            { id: 'avatarStudio.editor.ladder.noneExplained' },
            { count: review.total },
          )}
        </Typography>
      )}

      {form.ladderKind === 'count' && (
        <Alert severity="info">
          {formatMessage({ id: 'avatarStudio.editor.ladder.storedAsCount' })}
        </Alert>
      )}

      {form.ladderKind !== 'none' && (
        <PerOptionTierEditor
          tierCount={tierCount}
          rows={rows}
          isColour={form.kind === 'color'}
          onChange={onPerSlugChange}
          onSetAll={onSetAll}
        />
      )}

      {review.unreachable.length > 0 && (
        <Alert severity="warning">
          {formatMessage(
            { id: 'avatarStudio.editor.ladder.unreachable' },
            {
              count: review.unreachable.length,
              total: review.total,
              names: review.unreachable.slice(0, 3).join(', '),
              // The names are capped at three, so the count and the list would otherwise disagree.
              more: Math.max(0, review.unreachable.length - 3),
            },
          )}
        </Alert>
      )}

      {error && (
        <Alert severity="error">
          {sectionErrorText(error, (id) => formatMessage({ id }))}
        </Alert>
      )}
    </Stack>
  )
}
